/**
 * Marketing Studio breadcrumb — thin wrapper over the reusable MissionCommandBar.
 *
 * All rendering (responsive rail + live counters + contextual drawers) lives in
 * MissionCommandBar (canonical-breadcrumb.md §6 / AO-002). This wrapper only
 * supplies the Marketing config: the Grow-the-Brand mission (8-stage arc),
 * the Marketing workflows, the counts + stage-rows endpoints, and phase-gating.
 *
 * ROLLOUT: renders on ALL MARKETING_CANONICAL_PATHS (full Marketing rollout,
 * paco-approved 2026-07). The earlier one-leaf pilot gate is retired.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, ClipboardList, Wand2, Sparkles, BarChart3, Target, Layers, TrendingUp } from "lucide-react";
import { api } from "../../lib/api";
import {
  GROW_THE_BRAND_MISSION,
  MARKETING_PILLAR_WORKFLOWS,
  STAGE_OWNER_BADGE,
  detectMarketingPillar,
  detectMarketingStage,
  isMarketingStudioPath,
} from "../../config/missionRegistry";
import { recordMarketingLastVisited } from "../marketing-studio/MarketingStudioResumeChip";
import MissionCommandBar from "./MissionCommandBar";

const ICON_BY_NAME = { Search, ClipboardList, Wand2, Sparkles, BarChart3, Target, Layers, TrendingUp, __fallback: Search };

export { isMarketingStudioPath };

export default function MarketingStudioPillarsBreadcrumb() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ pillars: {}, children: {}, recent_24h: { pillars: {}, children: {} } });

  useEffect(() => {
    let cancelled = false;
    api.get("/marketing-studio/mission-counts")
      .then((r) => {
        if (cancelled || !r?.data) return;
        setCounts({
          pillars: r.data.pillars || {},
          children: r.data.children || {},
          recent_24h: r.data.recent_24h || { pillars: {}, children: {} },
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pathname]);

  useEffect(() => {
    const pillar = detectMarketingPillar(pathname);
    if (!pillar) return;
    const workflow = MARKETING_PILLAR_WORKFLOWS[pillar];
    if (!workflow) return;
    const child = workflow.items.find((c) => c.page === pathname);
    if (!child) return;
    recordMarketingLastVisited(pathname, pillar, child.id);
  }, [pathname]);

  if (!isMarketingStudioPath(pathname)) return null;

  return (
    <MissionCommandBar
      mission={GROW_THE_BRAND_MISSION}
      workflows={MARKETING_PILLAR_WORKFLOWS}
      homeStudio="marketing"
      activeStageId={detectMarketingStage(pathname)}
      activePillarId={detectMarketingPillar(pathname)}
      counts={counts}
      stageRowsEndpoint="/marketing-studio/stage-rows"
      iconByName={ICON_BY_NAME}
      ownerBadgeLabels={STAGE_OWNER_BADGE}
      slug="marketing-studio"
      drawerSlug="marketing-stage"
      onNavigate={navigate}
    />
  );
}
